import React, { useMemo } from 'react';
import { Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Translation } from 'react-i18next';
import { Check } from 'react-feather';
import { TFunction } from 'i18next';
import { ProfileService } from 'services/ProfileService';
import { useAuthContext } from 'AuthContext';
import { conf } from 'config';
import { codesLanguages } from 'store/configs/Languages';
import routes from 'store/configs/Routes';
import CardComponent from 'components/common/CardComponent';
import CardFieldRowComponent from 'components/common/CardFieldRowComponent';
import Spinner from 'components/shared/Spinner';

import './AccountComponent.scss';
import SignInLink from 'components/shared/SignInLink';

const AccountComponent: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { user, userLoading, profile, profileLoading, profileError } = useAuthContext();

  const customer = useMemo(() => profile?.customer, [profile?.customer]);
  const affiliateChosen = useMemo(() => profile?.affiliate && profile?.affiliate?.affiliateid, [profile?.affiliate]);

  const hasStandardsCommittees = useMemo(
    () =>
      profile &&
      profile.committee.standards.filter(
        (comm) => comm.committeeid !== conf.programCommitteeId && comm.status === conf.active
      ).length > 0,
    [profile]
  );

  const programComActive = useMemo(() => {
    const standards = profile?.committee?.standards;
    return standards ? ProfileService.hasActiveProgramCommittee(standards) : false;
  }, [profile?.committee?.standards]);

  return (
    <div id="account-page" className={`container ${userLoading || profileLoading ? 'emptySpacer' : ''} my-5`}>
      <Spinner loading={userLoading || profileLoading} transparent={false} className="h-100">
        {!user || !profile ? (
          <Alert severity="error" className="mb-3">
            Your session has expired or you are not logged in. Please <SignInLink>Sign In</SignInLink> and try again.
          </Alert>
        ) : profileError ? (
          <Alert severity="error" className="mb-3">
            Failed to load account. Please refresh the page or try again later.
          </Alert>
        ) : (
          <Translation>
            {(t: TFunction): JSX.Element => (
              <div id="accountComponent" className="container mt-3">
                <div className="row">
                  <div className="col-12 col-lg-6">
                    <CardComponent title={t('Profile')} editRoute={routes.account.editProfile}>
                      <CardFieldRowComponent
                        title={t('Full Name')}
                        value={customer?.firstname + ' ' + customer?.lastname}
                        includeDivider
                      />
                      <CardFieldRowComponent title={t('Company Name')} value={customer?.companyName} includeDivider />
                      <CardFieldRowComponent title={t('Job Title')} value={customer?.title} includeDivider />
                    </CardComponent>

                    <CardComponent title={t('Email')}>
                      <CardFieldRowComponent
                        title={t('Email')}
                        value={customer?.email}
                        valueNoCaps={true}
                        description={t('EmailMessage')}
                        includeDivider
                      />
                    </CardComponent>

                    <CardComponent title={t('Account Options')}>
                      <CardFieldRowComponent
                        title={t('Language')}
                        valueJSX={
                          <p className="text-dark text-capitalize mb-3">
                            {customer && customer.language in codesLanguages
                              ? codesLanguages[customer?.language as keyof typeof codesLanguages]
                              : customer?.language}
                            <br />
                            <a
                              href="#"
                              onClick={(e): void => {
                                e.preventDefault();
                                navigate(`/account/${routes.account.editLanguage}`);
                              }}
                            >
                              {t('Language Preference')}
                            </a>
                          </p>
                        }
                        description={t('LanguageMessage')}
                        includeDivider
                      />
                      <CardFieldRowComponent
                        valueJSX={
                          <a
                            href="#"
                            onClick={(e): void => {
                              e.preventDefault();
                              navigate(`/account/${routes.account.editPrivacyPolicy}`);
                            }}
                          >
                            {t('Privacy Policy')}
                          </a>
                        }
                        description={t('Privacy Description')}
                        includeDivider
                      />
                    </CardComponent>
                  </div>

                  <div className="col-12 col-lg-6">
                    {profile.customer?.isMembershipPrimary && (
                      <CardComponent title={t('Member Directory')}>
                        <CardFieldRowComponent title={t('Member Directory Profile')} includeDivider>
                          <p className="description text-muted">
                            Update the logo and edit your Member Directory Profile information.
                          </p>
                        </CardFieldRowComponent>
                        <a href={profile.customer?.urlname} target="_blank" rel="noopener noreferrer">
                          <button className="btn btn-primary superRadius float-right pt-2 pb-2 m-auto text-uppercase">
                            Update
                          </button>
                        </a>
                      </CardComponent>
                    )}

                    <CardComponent
                      title={t('Standards Committees')}
                      editRoute={
                        affiliateChosen
                          ? routes.account.committeesEditStandards
                          : routes.account.committeesEditAffiliate
                      }
                    >
                      {affiliateChosen ? (
                        <>
                          <CardFieldRowComponent
                            title={t('Affiliate Company')}
                            value={profile.affiliate.affiliateName}
                            includeDivider
                          >
                            <p className="description text-muted">
                              Please contact&nbsp;
                              <a
                                href="https://www.semi.org/en/semi-standards-staff-contacts"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                SEMI Standards
                              </a>
                              &nbsp;to change your voting affiliate.
                            </p>
                            <p className="description text-muted">
                              Please click{' '}
                              <a
                                href="http://downloads.semi.org/web/wstdsbal.nsf/StdCharters"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                HERE
                              </a>{' '}
                              for the charter and scope of all Global Technical Committees.
                            </p>
                          </CardFieldRowComponent>

                          {programComActive && (
                            <div className="row">
                              <div className="col-9 text-capitalize">
                                <p>
                                  <strong>Program Membership</strong>
                                </p>
                              </div>
                              <div className="col-3">
                                <div className="float-right">
                                  <Check />
                                </div>
                              </div>
                            </div>
                          )}

                          {hasStandardsCommittees ? (
                            profile.committee.standards.map((comm, i) => {
                              if (comm.committeeid === conf.programCommitteeId || comm.status !== conf.active)
                                return null;
                              return (
                                <div key={i} className="row">
                                  <div className="col-9 text-capitalize">
                                    <p>
                                      <strong>{comm.committeename}</strong>
                                    </p>
                                  </div>
                                  <div className="col-3">
                                    <div className="float-right">
                                      <Check />
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="row">
                              <div className="col-12">
                                <p>No selected committees.</p>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="row">
                          <div className="col-12">
                            <p>{t('Affiliate Message')}</p>
                          </div>
                        </div>
                      )}
                    </CardComponent>

                    <CardComponent title={t('Address')} editRoute={routes.account.editAddress}>
                      <CardFieldRowComponent
                        title={t('Home Address')}
                        valueJSX={
                          <address>
                            {customer?.billAddressee} <br />
                            {customer?.billAddress1} <br />
                            {[
                              customer?.billCity,
                              customer?.billState,
                              customer?.billCountry,
                              customer?.billZipcode,
                            ].join(' ')}
                          </address>
                        }
                        includeDivider
                      />
                    </CardComponent>

                    <CardComponent title={t('Phone')} editRoute={routes.account.editPhone}>
                      <CardFieldRowComponent title={t('Phone')} value={customer?.phone} includeDivider />
                    </CardComponent>
                  </div>
                </div>
              </div>
            )}
          </Translation>
        )}
      </Spinner>
    </div>
  );
};

export default AccountComponent;
